import { CSSProperties, VideoHTMLAttributes, forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { IPost } from "../../models/ICategory";
import FallBackImage from "./FallBackImage";
import Link, { LinkProps } from "next/link";
import { ImageProps } from 'next/image';
import { getPostFormattedDate, normalizeIsoDate } from "../../utils/date";
import { truncateText } from '../../utils/truncateText';
import Play from '../components/svg/FeedPlay';;


interface Props {
  locale: string;
  maxDescLength?: number;
  customTitleEl?: JSX.Element;
  h2?: boolean;
  data: {
    eager?: boolean;
    post: IPost & { imageProps?: Partial<ImageProps> };
    category: {
      title: string;
      slug: string;
      uniqueCode: string;
    }
  };
  className?: Partial<{
    wrapper: string;
    image: string;
    content: Partial<{
      category: string;
      meta: string;
      title: string;
    }>;
  }>;
  forceUpdate?: boolean;
  hideMeta?: boolean;
  hideCategory?: boolean;
  style?: Partial<CSSProperties>;
  device?: string;
  linkProps?: Partial<LinkProps>;
}



const ArticleBrief = forwardRef(({ className, style = {}, h2, customTitleEl, data, locale, maxDescLength, forceUpdate, hideCategory = false, hideMeta = false, device, linkProps }: Props, ref: any) => {
  const { post: { title, publishedOn, writers, image, thumbnail, video, imageProps, uniqueCode, type }, category, eager } = data;

  const isVideo = type === 1;

  const writer = writers[0];
  return (

    <div ref={ref} className={twMerge("w-full overflow-hidden flex items-start tablet:items-start desktop:items-start tablet:flex-col bg-transparent gap-x-[16px] dark:bg-transparent", className?.wrapper)} style={style}>
      <Link
        hrefLang={locale}
        href={category ? `/${category.uniqueCode || category.slug}/${uniqueCode}` : ''}
        title={data.post.title}
        className={twMerge(
          "grid place-items-center rounded-[8px] [&>*]:w-full w-full relative overflow-hidden h-full",
          isVideo && 'before:bg-black before:rounded-full before:h-[25%] before:w-auto before:aspect-square before:absolute before:top-[50%] before:left-[50%] before:translate-x-[-50%] before:translate-y-[-50%] before:z-[3] before:opacity-[0.7]',
          className?.image
        )}
        {...linkProps}
        >
        {isVideo ? <Play className='w-auto h-[20%] aspect-square z-[4] absolute' /> : null}
        {image || thumbnail ? <FallBackImage

        device={device}
          forceUpdate={forceUpdate}
          eager={eager}
          src={image}
          thumbnail={thumbnail}
          fill
          sizes='100vw'
          alt={data.post.imageAlt || ''}
          className="h-full object-cover aspect-square" 
          {...imageProps}
        /> : null}
      </Link>
      <div className={twMerge('h-fit w-full flex flex-col h3 dark:[&>*]:text-white leading-[166%] py-[12px] gap-y-[8px]')}>
        {!hideCategory && category ? <Link hrefLang={locale} href={`/${category.uniqueCode || category.slug}`} className={twMerge('text-secondary-200 w-fit h4', className?.content?.category)}>{category.title}</Link> : null}
        {hideMeta ? null : <div className={twMerge('flex items-center gap-x-[12px] dark:[&>*]:text-white mb-[4px] [&>*]:sm [&>*]:whitespace-nowrap flex-wrap', className?.content?.meta)}>
          {writer ? <Link hrefLang={locale} href={`/writers/${writer.uniqueCode}`}>{writer.name}</Link> : null}
          <span suppressHydrationWarning={true}>{getPostFormattedDate(normalizeIsoDate(publishedOn), locale)}</span>
        </div>}
        <Link hrefLang={locale} title={title} href={`/${category.uniqueCode || category.slug}/${uniqueCode}`} className='flex items-start'>
          {customTitleEl ? customTitleEl : (h2 ? <h2 className={twMerge('h3 !font-kufi whitespace-normal line-clamp-3',className?.content?.title)}>{truncateText(title, maxDescLength ?? 200)}</h2> : <h3 className={twMerge('whitespace-normal line-clamp-3',className?.content?.title)}>{truncateText(title, maxDescLength ?? 200)}</h3>)}
        </Link>
      </div>

    </div>
  )
});



export default ArticleBrief;